/**
 * Peer profiles — the one read of other people's profiles (open-access
 * lockdown, Step 6). Answers only about people the caller is connected to
 * (any room, any state), through the server's get_peer_profiles check:
 * whether their profile still exists, their tier, last active, and the card
 * basics (name, photo, city). A peer's phone, email or other raw fields never
 * come back.
 *
 * An id that isn't one of the caller's peers returns no row: "unknown", never
 * "deleted" — callers that latch a deletion must only act on
 * `profileExists === false`.
 */

import { supabase } from '../supabase';

/**
 * @param {string[]} profileUserIds
 * @returns {Promise<{rows: Map<string, Object>, error: Object|null}>}
 *   rows: id → { profileExists, accountStatus, lastActiveAt, name, photoUrl, currentCity,
 *   currentCityPlace }
 */
export const getPeerProfiles = async (profileUserIds) => {
  const ids = [...new Set((profileUserIds || []).filter(Boolean))];
  if (ids.length === 0) return { rows: new Map(), error: null };

  const { data, error } = await supabase.rpc('get_peer_profiles', { p_profile_user_ids: ids });
  if (error) {
    console.error('[PEERS] get_peer_profiles failed:', error.message);
    return { rows: new Map(), error };
  }
  const rows = new Map();
  for (const r of data || []) {
    rows.set(r.profile_user_id, {
      profileExists: r.profile_exists,
      accountStatus: r.account_status,
      lastActiveAt: r.last_active_at,
      name: r.name,
      photoUrl: r.photo_url,
      currentCity: r.current_city,
      currentCityPlace: r.current_city_place || null,
    });
  }
  return { rows, error: null };
};
